import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ReactComponent as LogoIcon } from "@/assets/logo/union_ver1.svg?react";
import {
  PageContainer,
  FormContainer,
  ForgotPasswordInputTextStyle,
  ForgotPasswordButtonStyle,
  HelperLinks,
  BackgroundGlow,
  InfoText,
} from "./styles";
import { useModal } from "store/modalStore";
import { sendPasswordResetLink, resetPassword, verifyEmailToken } from "@/apis/user/auth";
import { checkEmail, checkPassword, checkRepeatPassword } from "@/utils/userValidation";
import { Logo } from "styles/styles";
import { KoHeadline } from "styles/sharedStyles";

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [hasToken, setHasToken] = useState(false);
  const [isTokenVerifying, setIsTokenVerifying] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [errors, setErrors] = useState({
    email: null as string | null,
    password: null as string | null,
    repeatPassword: null as string | null,
  });
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openInfo } = useModal();

  // URL 파라미터에서 토큰과 이메일 확인 및 검증
  useEffect(() => {
    const token = searchParams.get('token');
    const emailFromUrl = searchParams.get('email');
    console.log('URL token:', token); // 디버깅용
    console.log('URL email:', emailFromUrl); // 디버깅용
    if (token && emailFromUrl) {
      setEmail(emailFromUrl);
      setHasToken(true);
      verifyToken(emailFromUrl, token);
    }
  }, [searchParams]);

  const verifyToken = async (email: string, token: string) => {
    setIsTokenVerifying(true);
    try {
      // 비밀번호 찾기용 토큰 검증
      const response = await verifyEmailToken(email, token, "changePassword");
      if (response.message === "success") {
        setIsTokenValid(true);
        // API 응답에서 받은 token을 authToken으로 설정
        if (response.data?.token) {
          setAuthToken(response.data.token);
          console.log('Auth token set:', response.data.token); // 디버깅용
        }
        console.log('Token verification success'); // 디버깅용
      } else {
        setIsTokenValid(false);
        openInfo({ title: "유효하지 않은 접근", message: "다시 비밀번호 찾기를 진행해주세요." });
      }
    } catch (error) {
      setIsTokenValid(false);
      openInfo({ title: "유효하지 않은 접근", message: "다시 비밀번호 찾기를 진행해주세요." });
      console.error('Token verification failed:', error);
    } finally {
      setIsTokenVerifying(false);
    }
  };

  const handleSendLink = async () => {
    const emailError = checkEmail(email);
    if (emailError) {
      setErrors((prev) => ({ ...prev, email: emailError }));
      return;
    }

    setErrors((prev) => ({ ...prev, email: null }));
    setIsSubmitting(true);

    try {
      const response = await sendPasswordResetLink(email);
      
      if (response.status === 200) {
        openInfo({
          title: "링크 전송 완료",
          message: "이메일로 비밀번호 재설정 링크가 전송되었습니다."
        });
      } else if (response.status === 400) {
        openInfo({
          title: "링크 전송 실패",
          message: "이메일 주소가 올바르지 않습니다."
        });
      } else {
        openInfo({
          title: "링크 전송 실패",
          message: "비밀번호 재설정 링크 전송에 실패했습니다. 다시 시도해주세요."
        });
      }
    } catch (error) {
      openInfo({
        title: "오류",
        message: "이메일 확인 중 오류가 발생했습니다."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSendLink();
  };

  const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!authToken) {
      openInfo({ title: "오류", message: "유효하지 않은 접근입니다." });
      return;
    }

    // 새 비밀번호 유효성 검사
    const passwordError = checkPassword(password);
    const repeatPasswordError = checkRepeatPassword(password, repeatPassword);
    
    if (passwordError || repeatPasswordError) {
      setErrors((prev) => ({
        ...prev,
        password: passwordError,
        repeatPassword: repeatPasswordError,
      }));
      return;
    }

    setIsSubmitting(true);

    const resetData = {
      authToken,
      password,
    };
    console.log('Sending reset data:', resetData); // 디버깅용

    try {
      const response = await resetPassword(resetData);

      if (response.status === 200) {
        openInfo({
          title: "비밀번호 변경 완료",
          message: "비밀번호가 성공적으로 변경되었습니다."
        });
        
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        openInfo({
          title: "비밀번호 변경 실패",
          message: "비밀번호 변경에 실패했습니다. 다시 시도해주세요."
        });
      }
    } catch (error) {
      openInfo({
        title: "오류",
        message: "비밀번호 변경 중 오류가 발생했습니다."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <BackgroundGlow />

      <FormContainer onSubmit={hasToken && isTokenValid ? handlePasswordSubmit : handleEmailSubmit}>
        <Logo onClick={() => navigate("/")}>
          <LogoIcon style={{ width: "32px", height: "30px" }} />
        </Logo>

        <KoHeadline style={{ fontWeight: 600, marginBottom: "8px" }}>
          비밀번호 찾기
        </KoHeadline>
        
        {!hasToken ? (
          // 이메일 입력 화면
          <>
            <InfoText>
              가입하신 이메일 주소로 비밀번호 재설정 링크를 보내드립니다.
            </InfoText>

            <ForgotPasswordInputTextStyle
              type="email"
              placeholder="이메일"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              error={!!errors.email}
            />
            {errors.email && (
              <div style={{ color: "#ff4d4f", fontSize: "14px", alignSelf: "flex-start" }}>
                {errors.email}
              </div>
            )}

            <ForgotPasswordButtonStyle
              type="submit"
              disabled={!email || isSubmitting}
            >
              {isSubmitting ? "전송 중..." : "비밀번호 재설정 링크 보내기"}
            </ForgotPasswordButtonStyle>
          </>
        ) : isTokenVerifying ? (
          // 토큰 검증 중
          <>
            <InfoText>
              토큰을 검증하고 있습니다...
            </InfoText>
          </>
        ) : isTokenValid ? (
          // 새 비밀번호 입력 화면 (토큰 유효)
          <>
            <InfoText>
              새로운 비밀번호를 입력해주세요.
            </InfoText>

            <ForgotPasswordInputTextStyle
              type="password"
              placeholder="새 비밀번호"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              error={!!errors.password}
            />
            {errors.password && (
              <div style={{ color: "#ff4d4f", fontSize: "14px", alignSelf: "flex-start" }}>
                {errors.password}
              </div>
            )}

            <ForgotPasswordInputTextStyle
              type="password"
              placeholder="비밀번호 확인"
              value={repeatPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRepeatPassword(e.target.value)}
              error={!!errors.repeatPassword}
            />
            {errors.repeatPassword && (
              <div style={{ color: "#ff4d4f", fontSize: "14px", alignSelf: "flex-start" }}>
                {errors.repeatPassword}
              </div>
            )}

            <ForgotPasswordButtonStyle
              type="submit"
              disabled={!password || !repeatPassword || isSubmitting}
            >
              {isSubmitting ? "변경 중..." : "비밀번호 변경"}
            </ForgotPasswordButtonStyle>
          </>
        ) : (
          // 토큰 무효
          <>
            <InfoText>
              유효하지 않은 접근입니다. 다시 비밀번호 찾기를 진행해주세요.
            </InfoText>
            <ForgotPasswordButtonStyle
              type="button"
              disabled={false}
              onClick={() => {
                setHasToken(false);
                setIsTokenValid(false);
                setAuthToken("");
                setPassword("");
                setRepeatPassword("");
                navigate("/forgot-password", { replace: true });
              }}
            >
              비밀번호 찾기 다시 하기
            </ForgotPasswordButtonStyle>
          </>
        )}

        <HelperLinks>
          <Link to="/login">로그인으로 돌아가기</Link>
        </HelperLinks>
      </FormContainer>
    </PageContainer>
  );
};

export default ForgotPasswordPage;